import React, { useState, useEffect, useRef } from 'react';
import { useSocket, CONNECTION_STATES } from './SocketContext';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    List,
    ListItem,
    Alert,
    CircularProgress
} from '@mui/material';

const ChatComponent = ({ chatMessages, onMessagesChange = () => {}, onAgentConnected = () => {}, conversationEnded = () => {}, disabled = false, onGsaEvent, blockNo }) => {
    // Announce connection when socket connects
    const { socket, connectionState, error, isConnected } = useSocket();
    const hasAnnouncedConnectRef = useRef(false);
    useEffect(() => {
        if (connectionState === CONNECTION_STATES.CONNECTED && !hasAnnouncedConnectRef.current) {
            hasAnnouncedConnectRef.current = true;
            console.log('Socket connected: sending SYSTEM user connected');
            onMessagesChange(msgs => [...msgs, { sender: 'SYSTEM', text: 'User connected.' }]);
        }
    }, [connectionState, onMessagesChange]);

    const [input, setInput] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);
    const [agentConnected, setAgentConnected] = useState(false);
    const [explanationPending, setExplanationPending] = useState(false);
    const lastMessageRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (!socket) return;

        // Handlers for socket events
        const handleUserConnect = ({ userId }) => onMessagesChange(msgs => [...msgs, { sender: 'SYSTEM', text: 'User connected.' }]);
        const handleAgentConnect = ({ agentId }) => {
            onMessagesChange(msgs => [...msgs, { sender: 'SYSTEM', text: `Agent connected (${agentId})` }]);
            setAgentConnected(true);
            onAgentConnected && onAgentConnected(agentId);
        };
        // Normalize agent messages (+ handle Explanation sender)
        const handleMessageForUser = (message) => {
            if (message.sender === 'Explanation') {
                setExplanationPending(false);
                // A10: emit a typed `shown` event so the server can record
                // when the Explanation bubble actually appeared in the chat
                // panel — pairs with the `update` event from socket/index.js
                // so the data team can compute render-latency deltas.
                if (typeof onGsaEvent === 'function') {
                    const d = message.explanationData || {};
                    onGsaEvent('shown', {
                        score: d.score ?? null,
                        label: d.label ?? null,
                        nearest_traits: d.nearest_traits ?? null,
                        has_error: !!d.error,
                        explanation_appeared_at: d.timing?.explanation_appeared_at || null,
                    });
                }
                return onMessagesChange(prev => [...prev, { sender: 'Explanation', text: message.text, explanationData: message.explanationData }]);
            }
            onMessagesChange(prev => {
                if (prev.some(m => m.sender === 'Agent' && m.text === message.text)) return prev;
                return [...prev, { sender: 'Agent', text: message.text }];
            });
        };
        // Explanation pending indicator
        const handleExplanationPending = () => setExplanationPending(true);
        // Additional crew output event
        const handleCrewOutput = (out) => onMessagesChange(prev => [...prev, { sender: 'Agent', text: out.text }]);
        const handleDisconnect = (reason) => onMessagesChange(msgs => [...msgs, { sender: 'SYSTEM', text: `Disconnected from chat server. Reason: ${reason||'unknown'}` }]);
        const handleEndConversation = (data) => {
            const { reason } = data;
            onMessagesChange(msgs => [...msgs, { sender: 'SYSTEM', text: `Conversation ended by agent. Reason: ${reason||'unknown'}` }]);
            if (conversationEnded) conversationEnded(reason);
        };

        // Register socket events
        socket.on('user_connected', handleUserConnect);
        socket.on('agent_connected', handleAgentConnect);
        socket.on('message_for_user', handleMessageForUser);
        socket.on('crew_output', handleCrewOutput);
        socket.on('crew_finished', handleEndConversation);
        socket.on('disconnect', handleDisconnect);
        socket.on('explanation_pending', handleExplanationPending);

        const handleBeforeUnload = (event) => {
            console.log('User is leaving the page');
            socket.emit('user_disconnected');
            event.preventDefault();
            event.returnValue = 'Are you sure you want to leave this page? The experiment will end.';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('popstate', handleBeforeUnload);

        return () => {
            socket.off('user_connected', handleUserConnect);
            socket.off('agent_connected', handleAgentConnect);
            socket.off('message_for_user', handleMessageForUser);
            socket.off('crew_output', handleCrewOutput);
            socket.off('crew_finished', handleEndConversation);
            socket.off('disconnect', handleDisconnect);
            socket.off('explanation_pending', handleExplanationPending);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('popstate', handleBeforeUnload);
        };
    }, [socket, onMessagesChange, onAgentConnected, conversationEnded]); // eslint-disable-line react-hooks/exhaustive-deps


    useEffect(() => {
        if (lastMessageRef.current?.scrollIntoView) {
            lastMessageRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [chatMessages]);

    const handleInputChange = (event) => {
        setInput(event.target.value);
    };

    const sendMessage = async () => {
        if (!socket || !isConnected || !input.trim()) return;

        try {
            setSendingMessage(true);
            const msg = { sender: 'User', text: input };
            onMessagesChange(msgs => [...msgs, msg]);

            setInput('');
            if (inputRef.current) {
                inputRef.current.focus();
            }

            const sendPromise = new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    reject(new Error('Message sending timed out'));
                }, 5000);

                socket.emit('message_from_user', msg, () => {
                    clearTimeout(timeout);
                    resolve();
                });
            });

            await sendPromise;
        } catch (err) {
            console.error('Error sending message:', err);
        } finally {
            setSendingMessage(false);
            // ensure input regains focus after send button click
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 0);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        sendMessage();
    };

    const renderConnectionStatus = () => {
        switch (connectionState) {
            case CONNECTION_STATES.CONNECTING:
                return (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Connecting to server...
                        <CircularProgress size={20} sx={{ ml: 2 }} />
                    </Alert>
                );
            case CONNECTION_STATES.DISCONNECTED:
                return (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Disconnected from server. Attempting to reconnect...
                    </Alert>
                );
            case CONNECTION_STATES.ERROR:
                return (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Connection error: {error ? error.message : 'Unknown error'}
                    </Alert>
                );
            default:
                return null;
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {!isConnected && renderConnectionStatus()}

            <Paper
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    mb: 2,
                    p: 2,
                    backgroundColor: '#fafafa',
                }}
            >
                <List>
                    {chatMessages.length > 0 ? (
                        chatMessages.map((message, index) => (
                            <ListItem
                                key={index}
                                ref={index === chatMessages.length - 1 ? lastMessageRef : null}
                                sx={{
                                    justifyContent: message.sender === 'User' ? 'flex-end' : 'flex-start',
                                }}
                            >
                                <Box
                                    sx={{
                                        maxWidth: message.sender === 'Explanation' ? '85%' : '70%',
                                        bgcolor: message.sender === 'User'
                                            ? '#e0f7fa'
                                            : message.sender === 'Explanation'
                                                ? '#ede7f6'
                                                : '#f1f8e9',
                                        p: 1,
                                        borderRadius: 1,
                                        ...(message.sender === 'Explanation' && {
                                            border: '1px solid #b39ddb',
                                            fontStyle: 'italic',
                                        }),
                                    }}
                                >
                                    <Typography variant="body2" color={message.sender === 'Explanation' ? 'secondary' : 'textSecondary'} sx={{ fontWeight: message.sender === 'Explanation' ? 600 : 400 }}>
                                        {message.sender === 'Explanation' ? '📊 Explanation' : message.sender}
                                    </Typography>
                                    <Typography variant="body1">{message.text}</Typography>
                                </Box>
                            </ListItem>
                        ))
                    ) : (
                        <ListItem>
                            <Typography variant="body2" color="textSecondary" align="center" sx={{ width: '100%' }}>
                                No messages yet
                            </Typography>
                        </ListItem>
                    )}
                </List>
                {explanationPending && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: '#ede7f6', borderRadius: 1, border: '1px solid #b39ddb', mb: 1 }}>
                        <CircularProgress size={16} color="secondary" />
                        <Typography variant="body2" color="secondary" sx={{ fontStyle: 'italic' }}>
                            Analysing message...
                        </Typography>
                    </Box>
                )}
            </Paper>

            <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex' }}>
                    <TextField
                        fullWidth
                        value={input}
                        onChange={handleInputChange}
                        placeholder="Type your message..."
                        variant="outlined"
                        size="small"
                        disabled={disabled || !isConnected || sendingMessage || !agentConnected}
                        inputRef={inputRef}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        sx={{ ml: 1 }}
                        disableElevation
                        disabled={disabled || !isConnected || sendingMessage || !input.trim() || !agentConnected}
                    >
                        {(!agentConnected || sendingMessage) ? <CircularProgress size={20} /> : 'Send'}
                    </Button>
                </Box>
            </form>
        </Box>
    );
};

export default ChatComponent;
